import {
  Context as PosContext,
  Customer,
  Discount,
  DiscountType,
  DiscountScope,
  Order,
  OrderLineItemDiscount,
  OrderLineItemAppliedDiscount,
  order as orderApi
} from '@squareup/sq-addons-web-sdk';

import { formatPhoneNumberToTenDigits } from 'utils/phoneNumber';

export type SearchRewardsResponse = {
  availableDiscounts: Discount[];
  appliedDiscounts: Discount[];
  updatedDiscounts: Discount[];
  unavailableDiscounts: string[];
  pointBalance: number;
};

export type LavaMoney = {
  amount: number;
  currency: string;
};

export type LavaDiscount = {
  uid?: string;
  name: string;
  type: DiscountType;
  scope: DiscountScope;
  amountMoney: LavaMoney;
  appliedMoney?: LavaMoney;
  lineItemId?: string;
  metadata: Record<string, string>;
};

export type LavaItem = {
  id: string;
  quantity: number;
  sku: string;
  amount: number;
  discounts_applied?: LavaItemDiscount[];
};

export type LavaItemDiscount = {
  amount: number;
  promotion_id?: string;
  slug?: string;
}

export type TransactionInput = {
  authorize_only: boolean;
  items: LavaItem[];
  location: string;
  membership_id: string;
  reference_id: string;
  discounts_applied?: LavaItemDiscount[];
};

type TransactionOutput = {
  authorize_only: boolean;
  items: LavaItem[];
  location: string;
  membership_id: string;
  reference_id: string;
};

interface LavaDiscountsApplied {
  [key: string]: any;
}

export async function getRewards(
    posContext: PosContext,
    referenceId: string,
    order: Order
): Promise<SearchRewardsResponse> {
  const lavaDomain = posContext.configurations.environmentUrl;

  const transactionInput: TransactionInput = {
    authorize_only: true,
    items: squareToLavaLineItems(order),
    location: order.locationId,
    reference_id: order.id || '',
    membership_id: referenceId,
  };

  const response = await fetch(`${lavaDomain}/v1/discounts`, {
    method: 'POST',
    body: JSON.stringify(transactionInput),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (response.status === 404) {
    throw new Error('Not found');
  }

  if (response.status !== 200 && response.status !== 201) {
    throw new Error('Error calling the Lava Discounts API');
  }

  let transactionOutput: TransactionOutput = await response.json();

  const availableDiscounts = [] as Discount[];
  const appliedDiscounts = [] as Discount[];
  const updatedDiscounts = [] as Discount[];
  const unavailableDiscounts = [] as string[];
  const pointBalance = 0;

  let lavaAppliedDiscounts: LavaDiscountsApplied = {}

  if (transactionOutput.items && transactionOutput.items.length > 0) {
    for (const item of transactionOutput.items) {
      if (!item.discounts_applied)
        continue
      for (const discountApplied of item.discounts_applied) {
        lavaAppliedDiscounts[item.id] = discountApplied;
      }
    }
  }

  if (order.lineItems) {
    for (const lineItem of order.lineItems) {
      let uid = lineItem.uid || '';
      if (uid === '' || !(uid in lavaAppliedDiscounts))
        continue

      // Calculate per item discount amount using floor division to avoid rounding errors
      // When the total discount doesn't divide evenly, use floor to ensure we don't exceed the total
      const quantity = Number(lineItem?.quantity ?? 1);
      const totalDiscountAmount = lavaAppliedDiscounts[uid].amount;
      const perItemDiscount = Math.floor(totalDiscountAmount / quantity);

      let lineItemDiscount: LavaDiscount = {
        lineItemId: uid,
        uid: uid,
        type: DiscountType.FIXED_AMOUNT,
        name: lavaAppliedDiscounts[uid].slug,
        amountMoney: { amount: perItemDiscount, currency: 'USD' },
        appliedMoney: { amount: lavaAppliedDiscounts[uid].amount, currency: 'USD' },
        metadata: { isLavaDiscount: "true", lavaRewardId: lavaAppliedDiscounts[uid].promotion_id },
        scope: DiscountScope.LINE_ITEM_PER_QUANTITY,
      };

      if (order.discounts && lineItem.appliedDiscounts) {
        const lavaDiscountsForLineItem = lineItem.appliedDiscounts
          ?.map((appliedDiscount: OrderLineItemAppliedDiscount) => order.discounts?.find((discount: OrderLineItemDiscount) => discount.uid === appliedDiscount.discountUid))
          .filter((discount?: OrderLineItemDiscount) => isLavaDiscount(discount));

        for (let i = 0; i < (lavaDiscountsForLineItem?.length || 0); i++) {
          const discount = lavaDiscountsForLineItem[i];
          if (discount) {
            const uid = discount.uid || '';
            if (uid !== '') {
              await orderApi.removeDiscount(uid);
            }
          }
        }
      }

      if (lineItemDiscount) {
        appliedDiscounts.push(lineItemDiscount);
      }
    }
  }

  return {
    availableDiscounts,
    appliedDiscounts,
    updatedDiscounts,
    unavailableDiscounts,
    pointBalance,
  };
}

export function squareToLavaLineItems(order: Order): LavaItem[] {
  let items: LavaItem[] = [];

  if (order.lineItems) {
    for (let i = 0; i < (order.lineItems?.length || 0); i++) {
      let lineItem = order.lineItems[i];
      if (!lineItem) {
        continue;
      }

      let item: LavaItem = {
        id: lineItem.uid || '',
        quantity: parseInt(lineItem.quantity),
        sku: lineItem.catalogObjectId || '',
        amount: Number(lineItem.basePriceMoney?.amount || 0),
      };

      items.push(item);
    }
  }

  return items
}

export function squareToLavaAppliedDiscounts(order: Order): LavaItemDiscount[] {
  let lavaItemDiscounts: LavaItemDiscount[] = [];

  if (order.discounts && order.discounts.length > 0) {
    order.discounts.forEach(orderLineItemDiscount => {
      let lavaItemDiscount: LavaItemDiscount = {
        amount: Number(orderLineItemDiscount.amountMoney?.amount) || 0,
        slug: orderLineItemDiscount?.name || '',
        promotion_id: orderLineItemDiscount?.metadata?.lavaRewardId || ''
      }
      lavaItemDiscounts.push(lavaItemDiscount)
    })
  }

  return lavaItemDiscounts
}

function isLavaDiscount(discount: OrderLineItemDiscount | undefined): boolean {
  return discount?.metadata?.isLavaDiscount === "true" ?? false;
}

export async function getMembership(
    posContext: PosContext,
    referenceId: string
): Promise<Customer> {
  const lavaDomain = posContext.configurations.environmentUrl;

  // Nima TODO: sanitize ref id?
  const response = await fetch(`${lavaDomain}/v1/memberships/${referenceId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (response.status === 404) {
    throw new Error('Not found');
  }

  if (response.status !== 200) {
    throw new Error('Error calling the Lava Get Member API');
  }

  const result = await response.json();

  const customer = {
    referenceId: result.id,
    emailAddress: result.customer.email,
    familyName: result.customer.last_name,
    givenName: result.customer.first_name,
    phoneNumber: '',
  };

  if (result.customer.mobile) {
    customer.phoneNumber = formatPhoneNumberToTenDigits(
        result.customer.mobile as string
    );
  }

  return customer;
}

export async function findMember(
    posContext: PosContext,
    referenceId: string
): Promise<Customer[]> {
  const lavaDomain = posContext.configurations.environmentUrl;

  const response = await fetch(
      `${lavaDomain}/v1/membership:find?f=${encodeURIComponent(referenceId)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
  );

  if (response.status === 404) {
    return [];
  }

  if (response.status !== 200) {
    throw new Error('Error calling the Lava Find Member API');
  }

  const result = await response.json();

  const customer = {
    referenceId: result.id,
    emailAddress: result.customer.email,
    familyName: result.customer.last_name,
    givenName: result.customer.first_name,
    phoneNumber: '',
  };

  if (result.customer.mobile) {
    customer.phoneNumber = formatPhoneNumberToTenDigits(
        result.customer.mobile as string
    );
  }

  let customers: Customer[] = new Array(1);
  customers[0] = customer;

  return customers;
}
